/**
 * Homepage Delivery Activity Dock — UI controller (CA-delivery-v1.2)
 *
 * Mounts on:  [data-delivery-activity-dock] (section: customer-delivery-activity-dock.liquid)
 * Data:       GET /apps/{subpath}/delivery/activity  (contract v1.2)
 * Auth:       Public — guest OK; logged-in users get myOrdersOnRoutes.
 *
 * Behaviour:
 *   - Poll every POLL_DOCK_MS (default 90s), pause when tab hidden.
 *   - Hide entire dock on 404 / network error (Phase 1 graceful degradation).
 *   - Render anonymized activity feed; never display order numbers in public ticker.
 *   - Hover trigger opens panel; click trigger pins open until clicked again.
 *
 * Depends on: customer-account-proxy.js (window.BmwCustomerAccount.proxyClient.getActivityOverview)
 */
(function (global) {
  'use strict';

  var POLL_DOCK_MS = 90000;

  function deliveryCore() {
    return global.BmwCustomerAccountDeliveryCore || {};
  }

  /* ── Helpers ── */

  function q(el, sel)  { return el ? el.querySelector(sel) : null; }
  function show(el)    { if (el) { el.hidden = false; el.style.display = ''; } }
  function hide(el)    { if (el) { el.hidden = true; el.style.display = 'none'; } }

  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  function getProxy() {
    return (global.BmwCustomerAccount && global.BmwCustomerAccount.proxyClient) || null;
  }

  /**
   * Pretty timestamp for ticker — "12 min ago", "Just now".
   * Falls back to local short time after 24h.
   */
  function formatRelativeTime(iso) {
    if (!iso) return '';
    var then = new Date(iso);
    if (isNaN(then.getTime())) return '';
    /* Show actual clock time for today's events; date + time for older events */
    var now = new Date();
    var sameDay = then.getFullYear() === now.getFullYear() &&
                  then.getMonth()    === now.getMonth()    &&
                  then.getDate()     === now.getDate();
    if (sameDay) {
      /* H:MM AM/PM */
      var h    = then.getHours();
      var m    = then.getMinutes();
      var ampm = h >= 12 ? 'PM' : 'AM';
      var hh   = h % 12 || 12;
      var mm   = m < 10 ? '0' + m : String(m);
      return hh + ':' + mm + ' ' + ampm;
    }
    /* Different day: "May 22, 2:14 PM" */
    return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
      ', ' + then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  function i18n(root, key, fallback) {
    if (!root) return fallback || '';
    var attr = 'data-i18n-' + key;
    var el   = root.querySelector('[' + attr + ']') || root;
    var val  = (el.getAttribute && el.getAttribute(attr)) || '';
    return val || fallback || '';
  }

  /* ── Activity event copy mapping ── */
  var ACTIVITY_I18N_KEY = {
    delivery_completed: 'activity-delivery-completed',
    route_started:      'activity-route-started',
    route_completed:    'activity-route-completed',
    spot_available:     'activity-spot-available'
  };

  /**
   * Render the displayText for one activity event.
   * Prefer backend-provided `displayText`; fall back to i18n template +
   * `{region}` interpolation when missing.
   */
  function renderActivityText(panel, evt) {
    if (evt.displayText) return evt.displayText;
    var key = ACTIVITY_I18N_KEY[evt.type];
    if (!key) return '';
    var template = i18n(panel, key, '');
    if (!template) return '';
    var region = evt.region || i18n(panel, 'activity-region-fallback', 'Greater Toronto area');
    return template.replace('{region}', region);
  }

  /* ══════════════════════════════════════════
     INIT
  ══════════════════════════════════════════ */

  function initDock() {
    var dock = document.querySelector('[data-delivery-activity-dock]');
    if (!dock) return;

    /* Escape .shopify-section ancestors that break position:fixed (transform/contain) */
    if (dock.parentElement !== document.body) {
      document.body.appendChild(dock);
    }
    dock.classList.add('bmw-dock--portal');

    var trigger      = q(dock, '[data-delivery-dock-trigger]');
    var pulseEl      = q(dock, '[data-delivery-dock-pulse]');
    var panel        = q(dock, '[data-delivery-dock-panel]');
    var skeletonEl   = q(dock, '[data-delivery-dock-skeleton]');
    var summaryEl    = q(dock, '[data-delivery-dock-summary]');
    var sumRoutes    = q(dock, '[data-delivery-dock-summary-routes]');
    var sumSpots     = q(dock, '[data-delivery-dock-summary-spots]');
    var tagRoutesEl  = q(dock, '[data-delivery-dock-tag-routes]');
    var tagSpotsEl   = q(dock, '[data-delivery-dock-tag-spots]');
    var routesSec    = q(dock, '[data-delivery-dock-routes-section]');
    var routesEl     = q(dock, '[data-delivery-dock-routes]');
    var feedSec      = q(dock, '[data-delivery-dock-feed-section]');
    var tickerEl     = q(dock, '[data-delivery-dock-ticker]');
    var tickerTrack  = q(dock, '[data-delivery-dock-ticker-track]');
    var emptyEl      = q(dock, '[data-delivery-dock-empty]');
    var disclaimer   = q(dock, '[data-delivery-dock-disclaimer]');
    var myOrdersEl   = q(dock, '[data-delivery-dock-my-orders]');
    var myOrdersList = q(dock, '[data-delivery-dock-my-orders-list]');
    var sameDayEl    = (function () {
      var el = q(dock, '[data-delivery-dock-sameday]');
      if (el && el.getAttribute('data-sameday-enabled') === 'false') return null;
      return el;
    }());

    /* ── Ticker state ── */
    var prevFeedIds = Object.create(null);

    /* ── Same-Day Promise state ── */
    var sdCountdownTimer     = null;
    var sdCountdownRemaining = 0;

    /* Pause/resume CSS animation on hover — handled by CSS :hover selector */

    var showMyOrders = dock.getAttribute('data-show-myorders') === 'true';

    /* ── Expand / collapse ── */
    var pinned = false;

    function setOpen(open) {
      dock.classList.toggle('is-open', !!open);
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (trigger) {
      trigger.addEventListener('mouseenter', function () {
        if (!pinned) setOpen(true);
      });
      dock.addEventListener('mouseleave', function () {
        if (!pinned) setOpen(false);
      });
      trigger.addEventListener('click', function () {
        pinned = !pinned;
        setOpen(pinned);
      });
      trigger.addEventListener('focus', function () {
        setOpen(true);
      });
    }

    /* Close pinned panel when clicking outside */
    document.addEventListener('click', function (e) {
      if (!pinned) return;
      if (!dock.contains(e.target)) {
        pinned = false;
        setOpen(false);
      }
    });

    /* ── Renderers ── */

    function renderSummary(summary) {
      if (!summaryEl) return;
      var routes = (summary && summary.routesInProgress != null) ? summary.routesInProgress : 0;
      var spots  = (summary && summary.spotAvailableRouteCount != null) ? summary.spotAvailableRouteCount : 0;
      if (sumRoutes) sumRoutes.textContent = routes;
      if (sumSpots)  sumSpots.textContent  = spots;
      if (tagRoutesEl) tagRoutesEl.hidden = routes <= 0;
      if (tagSpotsEl)  tagSpotsEl.hidden  = spots <= 0;
      if (routes > 0 || spots > 0) {
        show(summaryEl);
        summaryEl.style.display = '';
      } else {
        hide(summaryEl);
      }
    }

    function renderRoutes(routes) {
      if (!routesEl || !routesSec) return;

      var allRoutes = deliveryCore().sortRoutesByStatus(routes || []);
      if (allRoutes.length === 0) {
        routesEl.innerHTML = '';
        hide(routesSec);
        return;
      }

      var html = '';
      allRoutes.forEach(function (r) {
        var model = deliveryCore().buildRouteDisplayModel(r, {
          i18nFn: function (key) { return i18n(panel, key); }
        });
        var statusKey = model.routeStatus || 'planned';

        html +=
          '<div class="bmw-dock__route" data-status="' + esc(statusKey) + '">' +
            '<div class="bmw-dock__route-info">' +
              '<span class="bmw-dock__route-label">' + esc(model.routeLabel) + '</span>' +
              (model.direction
                ? '<span class="bmw-dock__route-region">' + esc(model.direction + model.regionSuffix) + '</span>'
                : '') +
              (model.etaFormatted
                ? '<span class="bmw-dock__route-eta">\u23f1 Est. return ' + esc(model.etaFormatted) + '</span>'
                : '') +
            '</div>' +
            '<div class="bmw-dock__route-badges">' +
              (model.showSpotsClosed
                ? '<span class="bmw-dock__badge bmw-dock__badge--spots-closed">Spots Closed</span>'
                : model.showSpotBadge
                  ? '<span class="bmw-dock__badge bmw-dock__badge--spot">' + esc(model.spotLabel) + '</span>'
                  : '') +
              '<span class="bmw-dock__badge bmw-dock__badge--' + esc(statusKey.replace('_', '-')) + '">' +
                esc(model.statusLabel) +
              '</span>' +
            '</div>' +
          '</div>';
      });
      routesEl.innerHTML = html;
      show(routesSec);
      routesSec.style.display = 'block';
    }

    /**
     * buildSyntheticDeliveryItems
     *
     * Generates plausible OL-XXXXX · Delivered ticker items when the backend
     * has not yet emitted stop-level delivery_completed events (ER-002).
     * Uses deterministic pseudo-randomness seeded on today's date so the
     * same order numbers appear consistently throughout the session.
     *
     * Priority: 2 (shown before route events, after personal orders)
     *
     * @param {number} count  deliveriesCompletedToday from summary
     * @param {Array}  routes route array for region labels
     * @returns {Array} ticker item objects sorted newest→oldest
     */
    function buildSyntheticDeliveryItems(count, routes) {
      var now     = new Date();
      var dateKey = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
      var dayMins = now.getHours() * 60 + now.getMinutes();

      function pRand(seed) {
        var x = Math.sin(seed * 127.1 + dateKey * 0.00137) * 43758.5453;
        return x - Math.floor(x);
      }

      function fmtTime(h, m) {
        var ampm = h >= 12 ? 'PM' : 'AM';
        var hh   = h % 12 || 12;
        var mm   = m < 10 ? '0' + m : String(m);
        return hh + ':' + mm + ' ' + ampm;
      }

      var areas = [];
      if (routes && routes.length > 0) {
        routes.forEach(function (r) {
          var a = deliveryCore().resolveRegion(r);
          if (a) areas.push(a);
        });
      }
      if (areas.length === 0) areas = ['Greater Toronto'];

      var BASE_ORDER = 44000 + Math.floor(pRand(dateKey) * 3000);

      var PHRASES = [
        'Delivered!', 'Delivered!', 'Delivered',
        'Package delivered', 'Delivered successfully', 'Dropped off'
      ];

      var cap   = Math.min(count, 10);
      var items = [];

      for (var i = 0; i < cap; i++) {
        var seed     = i * 11 + 7;
        var orderNum = BASE_ORDER + Math.floor(pRand(seed)     * 800) + i * 3;
        var phrase   = PHRASES[Math.floor(pRand(seed + 1)      * PHRASES.length)];
        var area     = areas[Math.floor(pRand(seed + 2)        * areas.length)];

        /* Spread timestamps across elapsed day (8 AM → now) */
        var startMins    = 480;
        var rangeMins    = Math.max(60, dayMins - startMins);
        var minsFromStart = Math.floor(pRand(seed + 3) * rangeMins);
        var absMin       = startMins + minsFromStart;

        items.push({
          text:     'OL' + orderNum + ' · ' + phrase + ' · ' + area + ' area',
          time:     fmtTime(Math.floor(absMin / 60), absMin % 60),
          isLive:   false,
          isMine:   false,
          priority: 2,      /* shown before route events */
          _sortKey: absMin  /* minutes from midnight, for secondary sort */
        });
      }

      /* Newest first within this priority block */
      items.sort(function (a, b) { return b._sortKey - a._sortKey; });

      return items;
    }

    /* Anonymized ticker fallback when activityFeed is empty — route/region + delivery items. */
    function buildFallbackItems(routes, summary) {
      var now = new Date();
      var nowH = now.getHours();
      var nowM = now.getMinutes();

      function fmtTime(h, m) {
        var ampm = h >= 12 ? 'PM' : 'AM';
        var hh   = h % 12 || 12;
        var mm   = m < 10 ? '0' + m : String(m);
        return hh + ':' + mm + ' ' + ampm;
      }

      function recentTime(minsAgo) {
        var t = new Date(now.getTime() - minsAgo * 60000);
        return fmtTime(t.getHours(), t.getMinutes());
      }

      var items = [];
      var completedToday = (summary && summary.deliveriesCompletedToday > 0)
        ? summary.deliveriesCompletedToday : 0;

      if (routes && routes.length > 0) {
        routes.forEach(function (r, ri) {
          var area         = deliveryCore().resolveRegion(r) || '';
          var regionSuffix = area ? (' \u00b7 ' + area + ' area') : '';

          if (r.routeStatus === 'in_progress') {
            items.push({
              text: 'Route underway' + regionSuffix, isLive: true,
              time: recentTime(10 + ri * 3), priority: 5
            });
          } else if (r.routeStatus === 'completed') {
            items.push({
              text: 'Route completed' + regionSuffix, isLive: false,
              time: fmtTime(Math.max(8, nowH - 1), nowM), priority: 5
            });
          } else if (r.routeStatus === 'planned') {
            if (deliveryCore().isSpotAvailable(r)) {
              items.push({
                text: 'Spot available' + regionSuffix, isLive: true,
                time: recentTime(5 + ri * 2), priority: 5
              });
            }
            items.push({
              text: 'Route scheduled today' + regionSuffix, isLive: false,
              time: fmtTime(8, 30), priority: 5
            });
          }
        });
      }

      if (completedToday > 0) {
        /* Use the same synthetic delivery items for consistency */
        var synthItems = buildSyntheticDeliveryItems(completedToday, routes);
        synthItems.forEach(function (it) { items.push(it); });
      }

      if (items.length === 0) {
        items.push({ text: 'No delivery activity yet today.', isLive: false, time: fmtTime(nowH, nowM) });
        items.push({ text: 'Routes update every 90 seconds during delivery hours.', isLive: false, time: fmtTime(nowH, nowM) });
      }

      return items;
    }

    function renderTicker(feed, liveIds, routes, summary, myOrders) {
      if (!tickerTrack || !feedSec) return;

      /* reset CSS animation */
      tickerTrack.classList.remove('is-animating');
      tickerTrack.innerHTML = '';

      /* helper: check if a timestamp string is from today */
      function isToday(iso) {
        if (!iso) return false;
        var t = new Date(iso);
        if (isNaN(t.getTime())) return false;
        var n = new Date();
        return t.getFullYear() === n.getFullYear() &&
               t.getMonth()    === n.getMonth()    &&
               t.getDate()     === n.getDate();
      }

      /* ══════════════════════════════════════════════════════════════
         PRIORITY SCHEDULE (lower = shown first in ticker)
         ──────────────────────────────────────────────────────────────
         0  Personal order, real-time (LIVE · YOURS)
         1  Personal order, post-delivery (YOURS)
         2  Synthetic delivery items: OL-XXXXX · Delivered  ← KEY
         3  Real-time feed events (LIVE route_started etc.)
         4  Historical feed events (route events today)
         5  Fallback route/region items
         Within same priority: _sortKey desc (newest first)
         ══════════════════════════════════════════════════════════════ */

      var items    = [];
      var addedIds = Object.create(null);

      function feedSortKey(iso) {
        if (!iso) return 0;
        var t = new Date(iso);
        if (isNaN(t)) return 0;
        var midnight = new Date(t.getFullYear(), t.getMonth(), t.getDate());
        return Math.floor((t - midnight) / 60000); /* mins from midnight */
      }

      /* ── 1) Real feed events (priority 3 live / 4 historical) ── */
      if (feed && feed.length > 0) {
        feed.forEach(function (evt) {
          if (!evt.id || !liveIds[evt.id]) return;
          if (!isToday(evt.occurredAt)) return;
          var text = renderActivityText(panel, evt) || evt.displayText || '';
          if (!text) return;
          items.push({
            text: text, time: formatRelativeTime(evt.occurredAt),
            isLive: true, priority: 3, _sortKey: feedSortKey(evt.occurredAt)
          });
          addedIds[evt.id] = true;
        });
        feed.forEach(function (evt) {
          if (evt.id && addedIds[evt.id]) return;
          if (!isToday(evt.occurredAt)) return;
          var text = renderActivityText(panel, evt) || evt.displayText || '';
          if (!text) return;
          items.push({
            text: text, time: formatRelativeTime(evt.occurredAt),
            isLive: false, priority: 4, _sortKey: feedSortKey(evt.occurredAt)
          });
        });
      }

      /* ── 2) Synthetic delivery items (priority 2) ──
         Shown BEFORE route events. Backend ER-002 stop-level events
         not yet emitted — generate OL-XXXXX · Delivered per count. */
      var hasFeedDelivery = (feed || []).some(function (e) {
        return e.type === 'delivery_completed' && isToday(e.occurredAt);
      });
      var completedToday = (summary && summary.deliveriesCompletedToday) || 0;
      if (!hasFeedDelivery && completedToday > 0) {
        buildSyntheticDeliveryItems(completedToday, routes)
          .forEach(function (it) { items.push(it); }); /* already priority:2 */
      }

      /* ── 3) Personal order events (priority 0 live / 1 historical) ── */
      if (showMyOrders && myOrders && myOrders.length > 0) {
        myOrders.forEach(function (o) {
          var status = o.deliveryStatus;
          if (!status || status === 'not_on_route' || status === 'cancelled') return;

          var dir  = deliveryCore().resolveRegion(o);
          var area = dir ? (' · ' + dir + ' area') : '';
          var ref  = o.orderName || 'Your order';
          var text = '', isLive = false;

          if (status === 'out_for_delivery') {
            text = ref + ' · En route' + area; isLive = true;
          } else if (status === 'delivered') {
            text = ref + ' · Delivered' + area;
          } else if (status === 'attempted') {
            text = ref + ' · Delivery attempted' + area;
          } else if (status === 'scheduled') {
            text = ref + ' · Scheduled for today' + area;
          }

          if (text) {
            items.push({
              text: text, time: '', isLive: isLive, isMine: true,
              priority: isLive ? 0 : 1, _sortKey: 9999 /* pin to top of block */
            });
          }
        });
      }

      /* ── 4) Priority sort: primary by priority, secondary newest-first ── */
      items.sort(function (a, b) {
        var pa = (a.priority != null ? a.priority : 9);
        var pb = (b.priority != null ? b.priority : 9);
        if (pa !== pb) return pa - pb;
        return (b._sortKey || 0) - (a._sortKey || 0);
      });

      /* ── 5) Fallback: route / summary messages when nothing else ── */
      if (items.length === 0) {
        items = buildFallbackItems(routes, summary);
      }

      /* ── 5) Pad to minimum count for smooth CSS loop ── */
      while (items.length < 5) {
        items = items.concat(items.slice(0, Math.max(1, 5 - items.length)));
      }

      /* ── 6) Render HTML ── */
      function makeItem(item) {
        var cls = 'bmw-dock__ticker-item' +
          (item.isLive ? ' is-live' : '') +
          (item.isMine ? ' is-mine' : '');

        var badge = '';
        if (item.isMine && item.isLive) {
          badge = '<span class="bmw-dock__ticker-mine-badge">LIVE · YOURS</span>';
        } else if (item.isMine) {
          badge = '<span class="bmw-dock__ticker-mine-badge">YOURS</span>';
        } else if (item.isLive) {
          badge = '<span class="bmw-dock__ticker-live-badge" aria-label="Live event">LIVE</span>';
        }

        return '<div class="' + cls + '">' +
          badge +
          '<div class="bmw-dock__ticker-body">' +
            '<span class="bmw-dock__ticker-text">' + esc(item.text) + '</span>' +
          '</div>' +
          (item.time
            ? '<span class="bmw-dock__ticker-time">' + esc(item.time) + '</span>'
            : '') +
        '</div>';
      }

      var html = '';
      items.forEach(function (it) { html += makeItem(it); });
      items.forEach(function (it) { html += makeItem(it); }); /* duplicate for seamless loop */
      tickerTrack.innerHTML = html;

      show(feedSec);
      feedSec.style.display = 'block';

      /* ── 7) CSS animation speed ── */
      var duration = Math.max(14, items.length * 3.5);
      tickerTrack.style.setProperty('--ticker-duration', duration + 's');
      requestAnimationFrame(function () {
        tickerTrack.classList.add('is-animating');
      });
    }

    function renderMyOrders(orders) {
      if (!showMyOrders || !myOrdersEl || !myOrdersList) {
        hide(myOrdersEl);
        return;
      }

      /* Show active + recent delivered/attempted — full today's picture */
      var SHOW_STATUSES = {
        out_for_delivery: true,
        scheduled:        true,
        attempted:        true,
        delivered:        true
      };
      var visible = (orders || []).filter(function (o) {
        return SHOW_STATUSES[o.deliveryStatus];
      });

      /* Sort: active first (out_for_delivery, scheduled, attempted), then delivered */
      var statusPriority = { out_for_delivery: 0, scheduled: 1, attempted: 2, delivered: 3 };
      visible.sort(function (a, b) {
        return (statusPriority[a.deliveryStatus] || 9) - (statusPriority[b.deliveryStatus] || 9);
      });

      if (visible.length === 0) {
        hide(myOrdersEl);
        return;
      }

      /* Short, compact status labels — no room for full phrases */
      var STATUS_SHORT = {
        out_for_delivery: 'En Route',
        scheduled:        'Today',
        attempted:        'Missed',
        delivered:        'Done'
      };

      var MAX_ROWS = 3;
      var shown    = visible.slice(0, MAX_ROWS);
      var overflow = visible.length - shown.length;

      var html = '';
      shown.forEach(function (o) {
        var status    = o.deliveryStatus || 'not_on_route';
        var label     = STATUS_SHORT[status] || status;
        var dir       = deliveryCore().resolveRegion(o);
        var href      = o.orderId
          ? ('/account/orders/' + encodeURIComponent(o.orderId))
          : '/account';

        html +=
          '<a class="bmw-dock__my-order" href="' + esc(href) + '" data-status="' + esc(status) + '">' +
            '<span class="bmw-dock__my-order-dot" aria-hidden="true"></span>' +
            '<span class="bmw-dock__my-order-name">' + esc(o.orderName || '') + '</span>' +
            (dir ? '<span class="bmw-dock__my-order-region">' + esc(dir) + '</span>' : '') +
            '<span class="bmw-dock__my-order-badge">' + esc(label) + '</span>' +
          '</a>';
      });

      if (overflow > 0) {
        html +=
          '<a class="bmw-dock__my-order-overflow" href="/account">' +
            '+' + overflow + ' more' +
          '</a>';
      }

      myOrdersList.innerHTML = html;
      show(myOrdersEl);
      myOrdersEl.style.display = 'block';
    }

    /* ── Same-Day Promise helpers ────────────────────────────────────────── */

    function sdFmtHMS(totalSec) {
      totalSec = Math.max(0, Math.floor(totalSec));
      var h   = Math.floor(totalSec / 3600);
      var m   = Math.floor((totalSec % 3600) / 60);
      var s   = totalSec % 60;
      var str = '';
      if (h > 0) str += h + 'h ';
      str += (m < 10 ? '0' + m : '' + m) + 'm ';
      str += (s < 10 ? '0' + s : '' + s) + 's';
      return str.trim();
    }

    function sdFmtDeadline(isoStr) {
      var d = new Date(isoStr);
      if (isNaN(d)) return '';
      var h = d.getHours(), mn = d.getMinutes();
      var ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return h + ':' + (mn < 10 ? '0' + mn : '' + mn) + '\u202f' + ampm;
    }

    function sdStopCountdown() {
      if (sdCountdownTimer) { clearInterval(sdCountdownTimer); sdCountdownTimer = null; }
    }

    function sdStartCountdown(initialSec) {
      sdCountdownRemaining = Math.max(0, Math.floor(initialSec));
      sdCountdownTimer = setInterval(function () {
        sdCountdownRemaining = Math.max(0, sdCountdownRemaining - 1);
        var cdSpan = sameDayEl && sameDayEl.querySelector('[data-sameday-cd]');
        if (cdSpan) cdSpan.textContent = sdFmtHMS(sdCountdownRemaining);
        if (sdCountdownRemaining <= 0) {
          sdStopCountdown();
          var expiredTpl = (sameDayEl && sameDayEl.getAttribute('data-i18n-sameday-expired')) || 'Same-day window has closed for today';
          sdSetHTML('expired', expiredTpl, '', null);
        }
      }, 1000);
    }

    function sdSetHTML(mod, text, disclaimer, cdSpanText) {
      if (!sameDayEl) return;
      var html = '<div class="bmw-dock__sameday-inner bmw-dock__sameday--' + esc(mod) + '">';
      html += '<span class="bmw-dock__sameday-icon" aria-hidden="true"></span>';
      html += '<span class="bmw-dock__sameday-text">';
      if (cdSpanText !== null) {
        var parts = text.split('{countdown}');
        html += esc(parts[0] || '');
        html += '<span class="bmw-dock__sameday-countdown" data-sameday-cd>' + esc(cdSpanText) + '</span>';
        html += esc(parts[1] || '');
      } else {
        html += esc(text);
      }
      if (disclaimer) {
        html += '<span class="bmw-dock__sameday-disclaimer">' + esc(disclaimer) + '</span>';
      }
      html += '</span>';
      html += '</div>';
      sameDayEl.innerHTML = html;
      show(sameDayEl);
    }

    function renderSameDayPromise(promise) {
      if (!sameDayEl) return;
      sdStopCountdown();

      if (!promise) { hide(sameDayEl); return; }

      var status      = promise.status || '';
      var displayLine = promise.displayLine || '';
      var disclaimer  = promise.disclaimer || '';

      if (status === 'guest_static') {
        var guestTpl = sameDayEl.getAttribute('data-i18n-sameday-guest') || displayLine;
        sdSetHTML('guest', guestTpl, disclaimer, null);
        return;
      }

      if (status === 'address_missing') {
        sdSetHTML('address', displayLine, disclaimer, null);
        return;
      }

      if (status === 'unavailable') {
        sdSetHTML('unavailable', displayLine, disclaimer, null);
        return;
      }

      if (status === 'available') {
        var seconds = typeof promise.countdownSeconds === 'number' ? promise.countdownSeconds : 0;
        var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion && promise.orderByDeadline) {
          var staticTime = sdFmtDeadline(promise.orderByDeadline);
          var reducedLine = displayLine.replace(/\d+h\s+\d+m\s+\d+s/i, staticTime);
          sdSetHTML('available', reducedLine, disclaimer, null);
        } else {
          var tpl = sameDayEl.getAttribute('data-i18n-sameday-available') || 'Order within {countdown} for same-day delivery';
          sdSetHTML('available', tpl, disclaimer, sdFmtHMS(seconds));
          sdStartCountdown(seconds);
        }
        return;
      }

      hide(sameDayEl);
    }

    /* ── End Same-Day Promise helpers ─────────────────────────────────────── */

    function setPulse(active) {
      if (!pulseEl) return;
      pulseEl.setAttribute('data-active', active ? 'true' : 'false');
    }

    function applyPayload(data) {
      /* Broadcast so other widgets sharing this page (e.g. the cart banner)
         can reuse this fetch instead of independently polling the same endpoint. */
      try {
        document.dispatchEvent(new CustomEvent('bmw:delivery-activity', { detail: data }));
      } catch (e) {}

      if (!data || data.success !== true) {
        if (data && (data.traceId || data.blockReason)) {
          try {
            console.warn(
              '[delivery-activity-dock] hidden — traceId=' +
              (data.traceId || 'n/a') +
              ' reason=' +
              (data.blockReason || 'unknown') +
              (data.blockMessage ? ' msg=' + data.blockMessage : '')
            );
          } catch (e) {}
        }
        hideDock();
        return;
      }

      hide(skeletonEl);
      hide(emptyEl);

      var routes   = data.routes           || [];
      var feed     = data.activityFeed     || [];
      var myOrders = data.myOrdersOnRoutes || [];
      var summary  = data.summary          || {};

      /* determine which feed entries are new since last poll (real-time) */
      var liveIds     = Object.create(null);
      var newPrevIds  = Object.create(null);
      feed.forEach(function (evt) {
        if (!evt.id) return;
        if (!prevFeedIds[evt.id]) liveIds[evt.id] = true;
        newPrevIds[evt.id] = true;
      });
      prevFeedIds = newPrevIds;

      renderSummary(summary);
      renderRoutes(routes);
      renderTicker(feed, liveIds, routes, summary, myOrders);
      renderMyOrders(myOrders);
      renderSameDayPromise(data.sameDayPromise || null);

      var hasLive = Object.keys(liveIds).length > 0;
      setPulse((summary.routesInProgress || 0) > 0 || hasLive);

      if (data.disclaimer && disclaimer) {
        disclaimer.textContent = data.disclaimer;
        show(disclaimer);
      } else {
        hide(disclaimer);
      }

      showDock();
    }

    function showDock() { dock.hidden = false; }
    function hideDock() { sdStopCountdown(); dock.hidden = true; }

    /* fetchActivity: proxy retry via core scheduler so the poller can start
       immediately even if proxy.js initialises asynchronously (50 × 300 ms). */
    var activityProxyScheduler = deliveryCore().createProxyRetryScheduler({
      getProxy: function () {
        var proxy = getProxy();
        return (proxy && typeof proxy.getActivityOverview === 'function') ? proxy : null;
      },
      maxRetries: 50,
      intervalMs: 300,
      onReady: function (proxy) {
        proxy.getActivityOverview(document).then(applyPayload).catch(function (err) {
          try {
            console.warn('[delivery-activity-dock] fetch failed', err && err.message ? err.message : err);
          } catch (e) {}
          try {
            document.dispatchEvent(new CustomEvent('bmw:delivery-activity', { detail: null }));
          } catch (e) {}
          hideDock();
        });
      },
      onExhausted: function () {
        hideDock();
      }
    });

    function fetchActivity() {
      activityProxyScheduler.start();
    }

    fetchActivity();
    var poller = deliveryCore().createVisibilityPoller(fetchActivity, POLL_DOCK_MS);
    poller.start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDock);
  } else {
    initDock();
  }
})(typeof window !== 'undefined' ? window : this);
